import { Button, Card, Input, Label, Textarea, useToast } from "@nfp-tracker/components";
import { Loader2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetOwnerContextCache } from "../hooks/useOwnerContext";
import { ROUTES } from "../constants/routes";
import { supabase } from "../integrations/supabase/client";
import {
  acceptInstructorInvite,
  loadInstructorProfile,
  upsertInstructorProfile,
} from "../integrations/supabase/instructor";
import { handleExternalError } from "../utils/errorSnackbar";

/**
 * Instructor equivalent of tracking onboarding: instructors have no cycle profile to build,
 * only an identity clients will recognize. Kept separate from `Onboarding` so the tracking
 * wizard doesn't have to grow a second, unrelated branch.
 *
 * This page never grants the `instructor` role — the role is provisioned out of band with the
 * service role (see INSTRUCTOR_ACCOUNTS.md). An account that reaches it without the role gets a
 * dead end explaining that, because `accept_instructor_invite()` would refuse it anyway.
 */
const InstructorSetup = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const pendingInviteId = searchParams.get("inviteId");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isInstructor, setIsInstructor] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [publicEmail, setPublicEmail] = useState("");
  const [bio, setBio] = useState("");

  useEffect(() => {
    const load = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate(ROUTES.login, { replace: true });
        return;
      }

      setUserId(user.id);

      const [{ data: account }, profile] = await Promise.all([
        supabase
          .from("user_accounts")
          .select("first_name, last_name, email, role")
          .eq("user_id", user.id)
          .maybeSingle(),
        loadInstructorProfile(user.id),
      ]);

      setIsInstructor((account as { role?: string } | null)?.role === "instructor");

      setDisplayName(
        profile?.displayName ||
          [account?.first_name, account?.last_name].filter(Boolean).join(" ").trim()
      );
      setCompanyName(profile?.companyName || "");
      setPublicEmail(profile?.publicEmail || account?.email || user.email || "");
      setBio(profile?.bio || "");
      setLoading(false);
    };

    void load();
  }, [navigate]);

  const handleSubmit = async () => {
    if (!userId) return;

    const trimmedName = displayName.trim();
    if (!trimmedName) {
      toast({
        title: "Name required",
        description: "Clients need a name to recognize you by.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      await upsertInstructorProfile({
        userId,
        displayName: trimmedName,
        companyName,
        publicEmail,
        bio,
      });

      if (pendingInviteId) {
        const result = await acceptInstructorInvite(pendingInviteId);
        if (!result.ok) {
          toast({
            title: "Profile saved",
            description:
              "We couldn't accept the invitation automatically — ask your client to resend it.",
            variant: "destructive",
          });
          resetOwnerContextCache(queryClient);
          navigate(ROUTES.instructorClients, { replace: true });
          return;
        }
      }

      toast({ title: "Instructor account ready" });
      resetOwnerContextCache(queryClient);
      navigate(ROUTES.instructorClients, { replace: true });
    } catch (error) {
      handleExternalError(toast, error, { logPrefix: "Error saving instructor profile:" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isInstructor) {
    return (
      <div className="flex min-h-full items-start justify-center bg-gradient-to-b from-background to-muted p-4 pt-6 pb-8 sm:items-center">
        <Card className="w-full max-w-md space-y-4 p-8 text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Instructor access not enabled</h1>
          <p className="text-sm text-muted-foreground">
            This account isn't set up as an instructor account. Contact support to have instructor
            access enabled, then come back to finish setup.
          </p>
          <Button variant="outline" className="w-full" onClick={() => navigate(ROUTES.dashboard)}>
            Back to your account
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-start justify-center bg-gradient-to-b from-background to-muted p-4 pt-6 pb-8 sm:items-center">
      <Card className="w-full max-w-md space-y-6 p-8">
        <div className="space-y-2 text-center">
          <Users className="mx-auto h-12 w-12 text-primary" />
          <h1 className="text-2xl font-bold">Set up your instructor account</h1>
          <p className="text-sm text-muted-foreground">
            Clients see this when they share their chart with you.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="instructor-display-name">Display name</Label>
            <Input
              id="instructor-display-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Jane Doe, FCP"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="instructor-company">Practice or organization (optional)</Label>
            <Input
              id="instructor-company"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="instructor-email">Contact email shown to clients</Label>
            <Input
              id="instructor-email"
              type="email"
              value={publicEmail}
              onChange={(event) => setPublicEmail(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="instructor-bio">Short bio (optional)</Label>
            <Textarea
              id="instructor-bio"
              value={bio}
              rows={3}
              onChange={(event) => setBio(event.target.value)}
            />
          </div>
        </div>

        <Button className="h-12 w-full" onClick={handleSubmit} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Finish setup
        </Button>
      </Card>
    </div>
  );
};

export default InstructorSetup;
